import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  type BuildData,
  type BuildLogs,
  type CachedScreenData,
  type CompanyData,
  type EnvVar,
  type InteractionLog,
  type PromptStep,
  type RunExecution,
  type RunExecutionData,
  type ServerExecutionParams,
  type Test,
  type TestSuiteData,
} from "@/utils/types";
import { useAtom, useSetAtom, useAtomValue } from "jotai";
import { customFirestore, customStorage } from "@/firebase";
import {
  getDownloadURL,
  ref,
  uploadBytes,
  uploadString,
} from "firebase/storage";
import {
  annotateScreenshot,
  b64toBlob,
  buildDynamicValuesForPrompt,
  callChangeProxyState,
  delay,
  downloadAndUploadToStorage,
  extractDynamicValuesByAligning,
  getInputValues,
  getPromptText,
  getSessionMutationAtom,
  hideEnvVarSecrets,
  hideNetworkLogsEnvVars,
  parseGptCommandsResponse,
  redactBase64ForLog,
  replaceItemFomArray,
  shouldUseAppetizeClone,
  templatizeWithDynamicValues,
  trackAmplitudeEvent,
  trackSentryException,
  uploadScreenshotToStorage,
  withPerfLog,
} from "@/utils/helpers";
import { updateDoc } from "@firebase/firestore";
import { deleteField, doc, runTransaction } from "firebase/firestore";
import { appetizeSessionStore, buildLogsAtomWithId } from "@/atoms";
import { useStepObservable } from "./use-step-observable";
import JSZip from "jszip";
import { RESET } from "jotai/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { testBuilderCustomAlertStateAtom } from "../test-builder-page";
import { AuthContext } from "@/auth/AuthContext";
import { type StorageReference } from "@firebase/storage";
import { type SimulatorSession } from "@/utils/simulator";
import { useStore } from "@tanstack/react-store";

interface ExecutionStepState {
  screenshot: string;
  voiceCommandUrl?: string;
  listenedAudioUrl?: string;
  gptCommands: string[];
  appetizeCommands: string[];
  actionHistory: string[];
  screenContent: string[];
  serverExecutionParams?: ServerExecutionParams;
}

interface RunState {
  id?: string;
  startedTimeInEpoch?: number;
  testId?: string;
  title: string;
  commands: string;
  promptSteps: PromptStep[];
  executionSteps: ExecutionStepState[];
  preRequests?: Array<{
    screenshot: string;
    item?: { curl: string; status: number; data: any };
  }>;
  teardownRequests?: Array<{
    screenshot: string;
    item?: { curl: string; status: number; data: any };
  }>;
}

interface ExecutionState {
  failedExecutions: ExecutionState[];
  isRunning: boolean;
  runs: RunState[];
  result?: string;
  resultContent?: string;
}

const defaultExecutionState: ExecutionState = {
  failedExecutions: [],
  isRunning: false,
  runs: [],
};

// Mirrors FAPI's _looks_markdown_authored (the markdown gate of
// _is_eligible_for_review): a test is auto-reviewed only if its tiptap
// `commands` contain a node other than the legacy ordered-list-of-steps. We key
// off `commands` (always present on the run) rather than the rendered
// globalTestTask, which is computed at execution time and isn't reliably on the
// run object at conclusion — using it caused eligible failed runs to be treated
// as non-eligible and recorded immediately, before the review verdict.
const looksMarkdownAuthored = (commandsStr?: string): boolean => {
  if (commandsStr == null || commandsStr.length === 0) return false;
  try {
    const doc = JSON.parse(commandsStr);
    const content = doc?.content;
    if (!Array.isArray(content)) return false;
    return content.some(
      (node: { type?: string }) => node?.type !== "orderedList"
    );
  } catch {
    return false;
  }
};

const useExecuteSteps = ({
  buildData,
  companyData,
  testSuite,
}: {
  companyData: CompanyData;
  buildData: BuildData;
  testSuite?: TestSuiteData;
}) => {
  const [executionState, setExecutionState] = useState<ExecutionState>(
    defaultExecutionState
  );
  const [parsedExecutionLogs, setParsedExecutionLogs] = useState<
    CachedScreenData[]
  >([]);

  const buildLogsAtom = buildLogsAtomWithId(buildData.id);
  const [buildLogsData, setBuildLogsData] = useAtom(buildLogsAtom);
  const settingsData = companyData.settings;
  const setTestBuilderCustomAlertState = useSetAtom(
    testBuilderCustomAlertStateAtom
  );

  const createSessionMutation = useAtomValue(
    getSessionMutationAtom(testSuite?.deviceProviderSettings)
  );

  const appetizeCloneSessionRef = useRef<SimulatorSession | undefined>(
    createSessionMutation.data
  );

  const appetizeSession = useStore(appetizeSessionStore, (state) => state);
  const currentSessionRef = useRef(appetizeSession);

  const fromEditor = testSuite == null;
  const usingAppetizeClone = shouldUseAppetizeClone({
    settingsData,
    buildData,
    fromEditor,
  });

  useEffect(() => {
    if (usingAppetizeClone) {
      appetizeCloneSessionRef.current = createSessionMutation.data;
    } else {
      currentSessionRef.current = appetizeSession;
    }
  }, [
    testSuite,
    createSessionMutation.data,
    usingAppetizeClone,
    appetizeSession,
  ]);

  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      console.log("will call stop onBeforeUnload");
      appetizeCloneSessionRef.current?.stop();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      console.log("will call stop");
      appetizeCloneSessionRef.current?.stop();
    };
  }, []);

  const queryClient = useQueryClient();
  const { user } = useContext<any>(AuthContext);

  const { createObservables } = useStepObservable({
    testSuite,
    buildData,
    companyData,
  });

  const [stopExecutionHandler, setStopExecutionHandler] = useState<
    (() => void) | null
  >(null);

  const interactionLogs = useRef<InteractionLog[]>([]);

  useEffect(() => {
    interactionLogs.current = buildLogsData?.interactionLogs ?? [];
  }, [buildLogsData.interactionLogs]);

  // Append one entry to a test's runHistory, capped at the 10 most recent.
  // Idempotent on run_ref so a re-run (same run_ref) never double-records.
  // Used only for non-auto-reviewed runs; reviewed runs are recorded by FAPI
  // at verdict time (the headless browser is gone before the verdict).
  const appendRunHistoryEntry = useCallback(
    async ({ testId, entry }: { testId: string; entry: RunExecution }) => {
      try {
        await runTransaction(customFirestore, async (transaction) => {
          const testDocRef = doc(customFirestore, "custom-tests", testId);
          const testDocument = await transaction.get(testDocRef);
          if (!testDocument.exists()) {
            throw new Error("Document does not exist!");
          }
          const currentRunHistory = (
            (testDocument.data()?.runHistory ?? []) as RunExecution[]
          ).sort((a, b) => b.finishedTimeInEpoch - a.finishedTimeInEpoch);
          if (currentRunHistory.some((e) => e.run_ref === entry.run_ref)) {
            return;
          }
          const recentRunHistory = currentRunHistory.slice(0, 9);
          recentRunHistory.unshift(entry);
          transaction.update(testDocRef, { runHistory: recentRunHistory });
        });
      } catch (e) {
        trackSentryException(e);
        console.log("Error occured when saving run history", e);
      }
    },
    []
  );

  const concludedParentRunMutation = useMutation({
    mutationFn: async ({
      status,
      statusMessage,
      run,
      executionState,
    }: {
      status: string;
      statusMessage: string;
      run: RunExecutionData;
      executionState?: ExecutionState;
    }) => {
      const date = new Date();
      const finishedTimeInEpoch = Math.ceil(date.getTime() / 1000);
      const envVars: EnvVar[] = settingsData.envVars ?? [];

      let videoRef: StorageReference | undefined;
      let generatedZipFile: Blob | undefined;

      let appetizeCloneTempVideoEndpointUrl: string | undefined;
      let sessionId: string | undefined;
      if (usingAppetizeClone) {
        try {
          if (testSuite?.deviceProviderSettings?.provider !== "browserstack") {
            sessionId = appetizeCloneSessionRef.current?.info.session_id;
            appetizeCloneTempVideoEndpointUrl = `${
              appetizeCloneSessionRef.current?.serverUrl
            }/session_recording/${sessionId!}`;
          }
          await appetizeCloneSessionRef.current?.stop();
          await delay(10000);
        } catch (e) {
          console.error("couldn't stop session", e);
        }
      } else {
        const zip = new JSZip();
        const getDifferenceArray = (arr) => {
          const differences = [arr[0].relativeTimestamp]; // First number as it is

          for (let i = 1; i < arr.length; i++) {
            const currentNumber = arr[i].relativeTimestamp;
            const previousNumber = arr[i - 1].relativeTimestamp;
            differences.push(currentNumber - previousNumber);
          }

          return differences;
        };

        const sortedFrames = buildLogsData.videoFrames.sort(
          (frameA, frameB) =>
            frameA.relativeTimestamp - frameB.relativeTimestamp
        );

        const differences =
          sortedFrames.length > 0 ? getDifferenceArray(sortedFrames) : [];

        const fileNames: string[] = [];

        sortedFrames.forEach(function (imageUint8Array, index) {
          const currentFileName = `image-${index}-${differences[index]}.jpeg`;
          fileNames.push(currentFileName);
          zip.file(currentFileName, imageUint8Array.buffer);
        });

        zip.file("files.txt", fileNames.join("\n"));
        generatedZipFile = await zip.generateAsync({ type: "blob" });

        videoRef = ref(
          customStorage,
          `videos/${testSuite!.id}/${run.id}/recording.zip`
        );
      }

      try {
        if (videoRef != undefined && generatedZipFile != undefined) {
          await uploadBytes(videoRef, generatedZipFile);
        }

        const logsRef = ref(
          customStorage,
          `logs/${testSuite!.id}/${run.id}/logs.json`
        );
        if (buildLogsData.logs.length > 0) {
          await uploadString(
            logsRef,
            JSON.stringify(
              buildLogsData.logs.map((log) => {
                return {
                  ...log,
                  message: hideEnvVarSecrets(log.message, envVars),
                };
              })
            )
          );
        }

        const networkLogsRef = ref(
          customStorage,
          `networkLogs/${testSuite!.id}/${run.id}/networkLogs.json`
        );
        if (buildLogsData.networkLogs.length > 0) {
          await uploadString(
            networkLogsRef,
            JSON.stringify(
              hideNetworkLogsEnvVars(buildLogsData.networkLogs, envVars)
            )
          );
        }

        const interactionLogsAux = interactionLogs.current;
        const interactionsLogsRef = ref(
          customStorage,
          `interactionsLogs/${testSuite!.id}/${run.id}/interactionsLogs.json`
        );
        if (interactionLogsAux.length > 0) {
          const uploadedScreenshots = await Promise.all(
            interactionLogsAux
              .filter((interactionLog) => interactionLog.screenshot != null)
              .map(async (interactionLog, index) => {
                const base64Screenshot = interactionLog.screenshot;
                const screenshotRef = ref(
                  customStorage,
                  `interactionsLogs/${testSuite!.id}/${run.id}/screenshot-${
                    index + 1
                  }.png`
                );
                const screenshotBlob = await b64toBlob(base64Screenshot);
                await uploadBytes(screenshotRef, screenshotBlob);
                return await getDownloadURL(ref(screenshotRef));
              })
          );

          const uploadedVoiceCommands = await Promise.all(
            interactionLogsAux
              .filter(
                (interactionLog) => interactionLog.voiceCommandUrl != null
              )
              .map(async (interactionLog, index) => {
                const fileExtension = interactionLog
                  .voiceCommandUrl!.split(".")
                  .at(-1);

                const storagePath = `interactionsLogs/${testSuite!.id}/${
                  run.id
                }/voice-command-${index + 1}.${fileExtension!}`;
                const uploadResult = await downloadAndUploadToStorage({
                  url: interactionLog.voiceCommandUrl!,
                  storagePath,
                });
                return uploadResult.downloadURL;
              })
          );

          const uploadedListenedAudio = await Promise.all(
            interactionLogsAux
              .filter(
                (interactionLog) => interactionLog.listenedAudioUrl != null
              )
              .map(async (interactionLog, index) => {
                const fileExtension = interactionLog
                  .listenedAudioUrl!.split(".")
                  .at(-1);

                const storagePath = `interactionsLogs/${testSuite!.id}/${
                  run.id
                }/listened-audio-${index + 1}.${fileExtension!}`;
                const uploadResult = await downloadAndUploadToStorage({
                  url: interactionLog.listenedAudioUrl!,
                  storagePath,
                });
                return uploadResult.downloadURL;
              })
          );

          await uploadString(
            interactionsLogsRef,
            JSON.stringify(
              interactionLogsAux
                .filter(
                  (interactionLog) =>
                    interactionLog.screenshot != null ||
                    interactionLog.voiceCommandUrl != null ||
                    interactionLog.listenedAudioUrl != null
                )
                .map<InteractionLog>((interactionsLog, index) => {
                  const {
                    screenshot,
                    voiceCommandUrl,
                    listenedAudioUrl,
                    ...interactionWithoutAssets
                  } = interactionsLog;

                  const screenshotIndex = interactionLogsAux
                    .filter((log) => log.screenshot != null)
                    .findIndex((log) => log === interactionsLog);

                  const voiceCommandIndex = interactionLogsAux
                    .filter((log) => log.voiceCommandUrl != null)
                    .findIndex((log) => log === interactionsLog);

                  const listenedAudioIndex = interactionLogsAux
                    .filter((log) => log.listenedAudioUrl != null)
                    .findIndex((log) => log === interactionsLog);

                  return {
                    ...{
                      ...interactionWithoutAssets,
                      gptCommands:
                        interactionsLog.gptCommands?.map((command: string) =>
                          hideEnvVarSecrets(command, envVars)
                        ) ?? [],
                    },
                    ...(screenshot != null &&
                      screenshotIndex >= 0 && {
                        screenshotUrl: uploadedScreenshots[screenshotIndex],
                      }),
                    ...(voiceCommandUrl != null &&
                      voiceCommandIndex >= 0 && {
                        voiceCommandUrl:
                          uploadedVoiceCommands[voiceCommandIndex],
                      }),
                    ...(listenedAudioUrl != null &&
                      listenedAudioIndex >= 0 && {
                        listenedAudioUrl:
                          uploadedListenedAudio[listenedAudioIndex],
                      }),
                  };
                })
            )
          );
        }

        if (!testSuite?.stealthMode) {
          // FAPI's auto-review loop only runs for cloud runs on markdown-
          // authored tests with the matching direction toggle on (failure
          // review for failed/blocked, success verification for succeeded).
          // For those, FAPI owns the run-history write: it records the entry
          // server-side once the legit/non-legit verdict lands, and only for
          // legit outcomes. We can't do it here — cloud runs execute inside a
          // headless browser the runner tears down at conclusion, before the
          // verdict. Everything else (non-reviewed) is recorded immediately.
          // Eligibility mirrors _is_eligible_for_review in the FAPI server.
          const isAutoReviewEligible =
            (settingsData.enableMarkdownEditor ?? false) &&
            looksMarkdownAuthored(run.runData?.commands) &&
            ((status === "succeeded" &&
              (settingsData.enableAutoSuccessVerification ?? false)) ||
              ((status === "failed" || status === "blocked") &&
                (settingsData.enableAutoFailureReviewLoop ?? false)));

          if (isAutoReviewEligible) {
            console.log(
              "Auto-review eligible — run history left to the FAPI verdict"
            );
          } else {
            console.log("Adding run doc to the run history collection");
            await appendRunHistoryEntry({
              testId: run.testId!,
              entry: {
                startedTimeInEpoch:
                  executionState != null
                    ? executionState.runs?.at(-1)?.startedTimeInEpoch ?? 0
                    : finishedTimeInEpoch,
                finishedTimeInEpoch,
                status,
                run_ref: run.id!,
                suite_ref: testSuite!.id,
              },
            });
          }
        }

        await updateDoc(
          doc(
            customFirestore,
            "custom-test-suite-runs",
            testSuite!.id,
            "runs",
            run.id!
          ),
          {
            status,
            ...(videoRef != null && { recordingPath: videoRef.fullPath }),
            ...(usingAppetizeClone &&
              appetizeCloneTempVideoEndpointUrl != null && {
                tempVideoEndpointUrl: appetizeCloneTempVideoEndpointUrl,
              }),
            ...(usingAppetizeClone &&
              sessionId != null && {
                sessionId,
              }),
            ...(executionState == null && {
              startedTimeInEpoch: finishedTimeInEpoch,
            }),
            finishedTimeInEpoch,
            statusMessage,
            logsPath:
              buildLogsData.logs.length > 0 ? logsRef.fullPath : deleteField(),
            networkLogsPath:
              buildLogsData.networkLogs.length > 0
                ? networkLogsRef.fullPath
                : deleteField(),
            interactionLogsPath:
              interactionLogsAux.length > 0
                ? interactionsLogsRef.fullPath
                : deleteField(),
          }
        );

        setExecutionState((prev) => ({
          ...prev,
          result: status === "succeeded" ? "Success" : "Failed",
          resultContent: statusMessage,
        }));
        stopRunning();
      } catch (error) {
        trackSentryException(error);
        console.error(error);
      }
    },
  });

  const concludedDependencyMutation = useMutation({
    mutationFn: async ({
      status,
      run,
      parentRun,
      statusMessage,
      executionState,
    }: {
      status: string;
      run: RunExecutionData;
      parentRun: RunExecutionData;
      statusMessage: string;
      executionState: ExecutionState;
    }) => {
      const date = new Date();
      const finishedTimeInEpoch = Math.ceil(date.getTime() / 1000);

      await updateDoc(
        doc(
          customFirestore,
          "custom-test-suite-runs",
          testSuite.id,
          "runs",
          parentRun.id!,
          "dependencies",
          run.id!
        ),
        {
          status,
          statusMessage,
          finishedTimeInEpoch,
        }
      );
      if (status !== "succeeded") {
        await concludedParentRunMutation.mutateAsync({
          status,
          run: parentRun,
          statusMessage,
          executionState,
        });
      }
    },
  });

  const startedRunningDependencyMutation = useMutation({
    mutationFn: async ({
      run,
      parentRun,
      startedTimeInEpoch,
    }: {
      run: RunExecutionData;
      parentRun: RunExecutionData;
      startedTimeInEpoch: number;
    }) => {
      await updateDoc(
        doc(
          customFirestore,
          "custom-test-suite-runs",
          testSuite.id,
          "runs",
          parentRun.id!,
          "dependencies",
          run.id!
        ),
        {
          status: "running",
          startedTimeInEpoch,
        }
      );
    },
  });

  const startedRunningParentRunMutation = useMutation({
    mutationFn: async ({
      run,
      startedTimeInEpoch,
    }: {
      run: RunExecutionData;
      startedTimeInEpoch: number;
    }) => {
      let deviceName;
      let deviceOS;
      let dimensions;
      if (usingAppetizeClone) {
        deviceName = appetizeCloneSessionRef.current?.device?.name;
        deviceOS = appetizeCloneSessionRef.current?.device?.os;
        dimensions = appetizeCloneSessionRef.current?.device.screen;
      } else {
        dimensions = {
          width: currentSessionRef.current.getData().device.screen.width,
          height: currentSessionRef.current.getData().device.screen.height,
        };
      }

      await updateDoc(
        doc(
          customFirestore,
          "custom-test-suite-runs",
          testSuite.id,
          "runs",
          run.id!
        ),
        {
          status: "running",
          startedTimeInEpoch,
          dimensions,
          ...(deviceName && { device: deviceName }),
          ...(deviceOS && { os: deviceOS }),
        }
      );
    },
  });

  const saveRunExecutionAsBaseline = useCallback(
    async ({
      parentRunTestId,
      runState,
      platform,
      device,
      currentRun,
      nextRuns,
    }: {
      parentRunTestId: string;
      runState: RunState;
      platform: string;
      device: string;
      currentRun?: RunExecutionData;
      nextRuns?: RunExecutionData[];
    }) => {
      console.log(
        "Calling saveRunExecutionAsBaseline with parameters",
        redactBase64ForLog({
          parentRunTestId,
          runState,
          platform,
          device,
        })
      );

      if (runState.testId == null) {
        console.log("Test ID is missing - not saving");
        return;
      }

      try {
        const testRef = doc(customFirestore, "custom-tests", runState.testId);
        const cachePath = `cachedSteps.${platform}.${device}`;

        // Clear the cache path first
        await updateDoc(testRef, {
          [cachePath]: [],
        });

        const currentTimestamp = new Date().getTime();

        // Resolve env vars and run input values once so each step can
        // templatize its rendered commands by replacing {{key}} renderings
        // with their {{key}} placeholder for cache reuse on subsequent runs.
        const envVars: EnvVar[] = companyData.settings?.envVars ?? [];
        const runInputValues =
          currentRun != null
            ? getInputValues({
                currentRun,
                nextRunsToExecute: nextRuns ?? [],
                envVars,
              })
            : [];

        // For markdown-format tests, the user-authored `{{...}}` placeholders
        // (timestamp, randomNumber(N), env vars, ...) live in the global
        // markdown blob, not in individual prompt steps. Align template vs
        // rendered markdown once so every per-step templatization below can
        // reuse the same `{ "randomNumber(6)": "847293", ... }` mapping.
        const globalMarkdownDynamicValues: Record<string, string> =
          currentRun?.globalTestTaskTemplate != null &&
          currentRun?.globalTestTask != null
            ? extractDynamicValuesByAligning(
                currentRun.globalTestTaskTemplate,
                currentRun.globalTestTask
              )
            : {};

        // Process all steps in parallel
        const cachedScreenDataPromises = runState.executionSteps.map(
          async (step, index) => {
            const {
              reasoning,
              commands,
              step_number,
              step_description,
              actions_description,
              fromCache,
              step_id,
              step_completed,
            } = parseGptCommandsResponse(step.gptCommands);

            const stepNumberInt = parseInt(step_number);

            // Locate the user-authored prompt step that produced this baseline
            // step. Prefer step_id (UUID), fall back to the step number's
            // positional index. We deliberately avoid getPromptText's
            // step_description fallback for the prompt we persist to the
            // cache: step_description is the model's output, which contains
            // already-rendered values and markdown escapes (\_, \*, ...) we
            // don't want to leak into the cached template.
            const findPromptStep = <T extends { id?: string }>(
              steps: T[] | undefined
            ): T | undefined => {
              if (steps == null) return undefined;
              if (step_id != null && step_id.length > 0) {
                const byId = steps.find((s) => s.id === step_id);
                if (byId != null) return byId;
              }
              if (Number.isFinite(stepNumberInt) && stepNumberInt > 0) {
                return steps[stepNumberInt - 1];
              }
              return undefined;
            };

            const matchingPromptStep = findPromptStep(runState.promptSteps);
            const renderedPromptStep = findPromptStep(
              step.serverExecutionParams?.promptSteps
            );
            const promptTemplate = matchingPromptStep?.text ?? "";

            // Build a {{key}} -> rendered value map for this step so its
            // rendered gpt/appetize commands can be reverse-substituted into
            // templated form. Aligning the template promptStep against its
            // rendered counterpart captures volatile values like
            // {{timestamp}} and {{randomNumberInRange(...)}} too. Static
            // env/input lookup fills in any keys alignment may have missed.
            const alignedDynamicValues =
              promptTemplate.length > 0 && renderedPromptStep != null
                ? extractDynamicValuesByAligning(
                    promptTemplate,
                    renderedPromptStep.text
                  )
                : {};
            const fallbackDynamicValues = buildDynamicValuesForPrompt({
              promptTemplate,
              envVars,
              runInputValues,
            });
            // Order matters: per-step alignment (most specific) wins, then
            // per-step env/input fallback, then the markdown-wide map for
            // markdown tests whose dynamic values aren't tied to any single
            // prompt step.
            const dynamicValues: Record<string, string> = {
              ...globalMarkdownDynamicValues,
              ...fallbackDynamicValues,
              ...alignedDynamicValues,
            };

            // Best-effort fallback when we could not locate a prompt step: at
            // least templatize whatever string getPromptText returns, so any
            // rendered values still get rebound to their {{key}} placeholder.
            const fallbackPromptText = getPromptText(
              stepNumberInt,
              runState?.commands,
              step_description,
              step_id,
              runState?.promptSteps ?? []
            );
            const promptToPersist =
              matchingPromptStep != null
                ? promptTemplate
                : templatizeWithDynamicValues(
                    fallbackPromptText ?? "",
                    dynamicValues
                  );

            const renderedAppetizeCommand =
              step.appetizeCommands?.join("\n") ?? "";
            const templatedGptCommand = templatizeWithDynamicValues(
              commands ?? "",
              dynamicValues
            );
            const templatedAppetizeCommand = templatizeWithDynamicValues(
              renderedAppetizeCommand,
              dynamicValues
            );
            // The model's reasoning and actions_description text routinely
            // echo the rendered values it just acted on (e.g. "typing
            // 1778057657+email"). If we cache them verbatim those baseline
            // values bleed into the next run's action_history and the model
            // reasons against stale data. Reverse-substitute them back into
            // {{key}} form here so cache hits can re-resolve to the current
            // run's values via parsePrompt at runtime.
            const templatedReasoning = templatizeWithDynamicValues(
              reasoning ?? "",
              dynamicValues
            );
            const templatedActionsDescription = templatizeWithDynamicValues(
              actions_description ?? "",
              dynamicValues
            );

            // Create unique timestamp for each step to avoid collisions
            const stepTimestamp = currentTimestamp + index;

            // Parallel upload of all assets for this step
            const [
              screenshotLink,
              voiceCommandFirebaseStorageUrl,
              listenedAudioFirebaseStorageUrl,
            ] = await Promise.all([
              // Screenshot upload
              uploadScreenshotToStorage(
                step.screenshot,
                `cachedScreenshots/${runState.testId}/${platform}-${device}-cachedScreenshot-${stepTimestamp}.jpg`
              ),

              // Voice command upload (if exists)
              step.voiceCommandUrl != null
                ? (async () => {
                    const fileExtension = step.voiceCommandUrl
                      .split(".")
                      .at(-1);
                    const data = await downloadAndUploadToStorage({
                      url: step.voiceCommandUrl,
                      storagePath: `voiceCommands/${runState.testId}/${platform}-${device}-${stepTimestamp}.${fileExtension}`,
                    });
                    console.log("uploaded voice command", data);
                    return data.downloadURL;
                  })()
                : Promise.resolve(undefined),

              // Listened audio upload (if exists)
              step.listenedAudioUrl != null
                ? (async () => {
                    const fileExtension = step.listenedAudioUrl
                      .split(".")
                      .at(-1);
                    const data = await downloadAndUploadToStorage({
                      url: step.listenedAudioUrl,
                      storagePath: `listenedAudios/${runState.testId}/${platform}-${device}-${stepTimestamp}.${fileExtension}`,
                    });
                    console.log("uploaded listened audio", data);
                    return data.downloadURL;
                  })()
                : Promise.resolve(undefined),
            ]);

            return {
              screenshotUrl: screenshotLink,
              gptCommand: templatedGptCommand,
              appetizeCommand: templatedAppetizeCommand,
              stepNumber: step_number ?? "",
              // Persist the user-authored prompt step's id (UUID) so the
              // cache lookup at runtime - which keys off nextPromptStep.id -
              // can match. The model occasionally echoes back the step
              // number instead of the id (e.g. step_id: 1), which would
              // otherwise prevent any cache hit.
              stepId: matchingPromptStep?.id ?? step_id ?? "",
              stepCompleted: step_completed ?? false,
              prompt: promptToPersist ?? "",
              reasoning: templatedReasoning,
              fromCache: fromCache ?? false,
              actionsDescription: templatedActionsDescription,
              ...(Object.keys(dynamicValues).length > 0 && {
                dynamicValues,
              }),
              ...(voiceCommandFirebaseStorageUrl != null && {
                voiceCommandUrl: voiceCommandFirebaseStorageUrl,
              }),
              ...(listenedAudioFirebaseStorageUrl != null && {
                listenedAudioUrl: listenedAudioFirebaseStorageUrl,
              }),
            };
          }
        );

        // Wait for all steps to complete
        const cachedScreenData = await Promise.all(cachedScreenDataPromises);

        // Update Firestore with all cached data
        await updateDoc(testRef, {
          [cachePath]: cachedScreenData,
        });

        console.log(`Successfully saved execution as a new baseline`);

        // Update query cache
        const fromEditor = testSuite == null;
        if (fromEditor) {
          const test: Test | undefined = queryClient.getQueryData([
            "getTestForTestEditor",
            parentRunTestId,
          ]);
          if (test != null) {
            let newData;
            if (parentRunTestId === runState.testId) {
              newData = {
                cachedSteps: {
                  [platform]: {
                    [device]: cachedScreenData,
                  },
                },
              };
            } else {
              newData = {
                localDependenciesCacheSteps: [
                  ...(test.localDependenciesCacheSteps != null
                    ? test.localDependenciesCacheSteps
                    : []),

                  ...cachedScreenData.map((cachedRun) => ({
                    ...cachedRun,
                    title: runState.title,
                  })),
                ],
              };
            }

            const updatedTest = {
              ...test,
              ...newData,
            };

            queryClient.setQueryData(
              ["getTestForTestEditor", parentRunTestId],
              updatedTest
            );
          }
        }
      } catch (e) {
        trackSentryException(e);
        console.log("Error saving run execution as baseline", e);
      }
    },
    [testSuite]
  );

  const handleConcludeExecution = useCallback(
    async (
      event: any,
      runs: RunExecutionData[],
      executionState: ExecutionState,
      companyName: string
    ) => {
      const fromEditor = testSuite == null;
      const isParentRun = event.data.parentRun == null;

      if (event.data.status === "succeeded") {
        const parentRunTestId = runs.at(-1)!.testId!;
        const isExecutedPartially =
          fromEditor && runs[0]?.isPartialExecution === true;
        const isRunUntilExecution = event.data.run.isRunUntilExecution === true;

        // TODO note that saving baseline from cloud runs might lead to issues
        if (!isExecutedPartially && !isRunUntilExecution) {
          await withPerfLog("SaveRunExecutionAsBaseline", async () => {
            const currentRunIndex = runs.findIndex(
              (r) => r === event.data.run
            );
            const nextRuns =
              currentRunIndex >= 0 ? runs.slice(currentRunIndex + 1) : [];
            await saveRunExecutionAsBaseline({
              parentRunTestId,
              platform: event.data.run.platform,
              device: event.data.run.device,
              runState: executionState.runs.at(-1)!,
              currentRun: event.data.run as RunExecutionData | undefined,
              nextRuns,
            });
          });
        }
      }

      if (fromEditor) {
        if (isParentRun || event.data.status !== "succeeded") {
          let notificationMessage: string | null = null;
          if (event.data.run.isRunUntilExecution === true) {
            notificationMessage = "Run until execution completed";
          }
          if (event.data.status !== "succeeded") {
            notificationMessage = "Test failed";
          }
          if (notificationMessage != null) {
            const notification = new Notification(notificationMessage, {
              body: "The execution concluded",
            });

            notification.onshow = () => {
              alert(notificationMessage);
            };
          }

          setTestBuilderCustomAlertState({
            open: true,
            type:
              event.data.status === "succeeded"
                ? "success"
                : event.data.status === "blocked"
                ? "warning"
                : "error",
            title:
              event.data.status === "succeeded"
                ? "Success"
                : "Something went wrong",
            description: event.data.message,
            autoclose: false,
          });

          setExecutionState((prev) => ({
            ...prev,
            result: event.data.status === "succeeded" ? "Success" : "Failed",
            resultContent: event.data.message,
          }));
          stopRunning();
        }
      } else {
        if (isParentRun) {
          await withPerfLog(
            "ConcludedParentRunMutation",
            async () =>
              await concludedParentRunMutation.mutateAsync({
                run: event.data.run,
                status: event.data.status,
                statusMessage: event.data.message,
                executionState,
              })
          );
        } else {
          await withPerfLog(
            "ConcludedDependencyMutation",
            async () =>
              await concludedDependencyMutation.mutateAsync({
                run: event.data.run,
                parentRun: event.data.parentRun,
                status: event.data.status,
                statusMessage: event.data.message,
                executionState,
              })
          );
        }
      }

      if (isParentRun) {
        const run = event.data.run;
        const currentTestId = run.testId ?? run.custom_test_id;
        trackAmplitudeEvent({
          eventType: `Parent run execution finished`,
          eventProperties: {
            partialExecution: run.isPartialExecution ?? false,
            runUntilExecution: run.isRunUntilExecution ?? false,
            cloudRun: !fromEditor,
            withDependencies: runs.length > 1,
            platform: run.platform,
            device: run.device,
            status: event.data.status,
            ...(currentTestId !== undefined && {
              testId: currentTestId,
            }),
          },
          groups: { organisation: companyName },
        });
      }
    },
    [
      testSuite,
      saveRunExecutionAsBaseline,
      companyData,
      buildData,
      usingAppetizeClone,
    ]
  );

  const setProcessingText = useCallback((text: string) => {
    const processingText = document.getElementById("stepProcessingDynamicText");
    if (processingText != null) {
      processingText.innerText = text;
    } else {
      const overlay = document.getElementById("overlay");
      let processingDiv = overlay.querySelector(".processing");

      if (processingDiv == null) {
        processingDiv = document.createElement("div");
        processingDiv.className =
          "processing absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center hidden";

        const processingText = document.createElement("p");
        processingText.innerText = text;
        processingText.className = "text-white text-lg";
        processingText.id = "stepProcessingDynamicText";

        processingDiv.appendChild(processingText);
        overlay.appendChild(processingDiv);
      }
      const scanLine = overlay.firstChild;

      overlay.style.display = "block";
      processingDiv.style.display = "block";
      scanLine.style.display = "none";
    }
  }, []);

  const startScanning = useCallback(() => {
    const overlay = document.getElementById("overlay");
    const scanLine = overlay.firstChild;
    const processingDiv = overlay.querySelector(".processing");

    // Reset elements' styles
    overlay.style.display = "block";
    scanLine.style.display = "block";
    if (processingDiv != null) {
      processingDiv.style.display = "none";
    }

    setTimeout(() => {
      scanLine.style.display = "none";
      if (processingDiv != null) {
        processingDiv.style.display = "block";
      }

      // Run your OCR process here
      // After the OCR process is done, hide the overlay
      // overlay.style.display = 'none';
    }, 5000);
  }, []);

  const startExecution = useCallback(
    ({
      runs,
      enableRetry,
    }: {
      runs: RunExecutionData[];
      enableRetry?: boolean;
    }) => {
      let auxExecutionState: ExecutionState = defaultExecutionState;

      const { mainObservable, stopObservable } = createObservables({
        runs,
        enableRetry: enableRetry ?? false,
      });
      setStopExecutionHandler((_) => {
        return () => {
          stopObservable();
          stopRunning();
        };
      });
      const subscription = mainObservable.subscribe({
        next: async (event) => {
          const fromEditor = testSuite == null;
          console.log("event", redactBase64ForLog(event));
          const date = new Date();
          const heartbeatElement = document.getElementById("heartbeat");
          if (heartbeatElement != null) {
            heartbeatElement.innerText = `${event.type},${date.getTime()}`;
          }

          if (event.type === "RUN_EXECUTION_STARTED") {
            const date = new Date();
            const startedTimeInEpoch = Math.ceil(date.getTime() / 1000);

            auxExecutionState = {
              ...auxExecutionState,
              runs: [
                ...auxExecutionState.runs,
                {
                  title: event.data.title,
                  executionSteps: [],
                  commands: event.data.commands,
                  promptSteps: event.data.promptSteps,
                  testId: event.data.testId,
                  startedTimeInEpoch,
                },
              ],
            };

            setExecutionState(auxExecutionState);

            const runIndex = event.data.runIndex;
            if (!fromEditor) {
              const parentRunIndex = runs.length - 1;

              if (runIndex === 0) {
                startedRunningParentRunMutation.mutate({
                  run: runs[parentRunIndex],
                  startedTimeInEpoch,
                });
              }

              if (
                (runIndex === 0 && runs.length > 1) ||
                runIndex !== parentRunIndex
              ) {
                startedRunningDependencyMutation.mutate({
                  run: runs[runIndex],
                  parentRun: runs[parentRunIndex],
                  startedTimeInEpoch,
                });
              }
            }
            if (runIndex === 0) {
              const run = runs[runIndex];
              const currentTestId = run.testId;

              trackAmplitudeEvent({
                eventType: `Parent run execution started`,
                eventProperties: {
                  partialExecution: runs[runIndex].isPartialExecution ?? false,
                  runUntilExecution:
                    runs[runIndex].isRunUntilExecution ?? false,
                  cloudRun: !fromEditor,
                  withDependencies: runs.length > 1,
                  platform: runs[runIndex].platform,
                  device: runs[runIndex].device,
                  ...(currentTestId !== undefined && {
                    testId: currentTestId,
                  }),
                },
                groups: { organisation: companyData.name },
              });
            }
          } else if (event.type === "STEP_PROCESSING_TEXT_UPDATED") {
            if (event.data.text === "Analysing...") {
              startScanning();
            }
            setProcessingText(event.data.text);
          } else if (event.type === "NEW_STEP_INIT") {
            const auxRun =
              auxExecutionState.runs[auxExecutionState.runs.length - 1];
            const newRun: RunState = {
              ...auxRun,
              executionSteps: [
                ...auxRun.executionSteps,
                {
                  screenshot: event.data.screenshot,
                  gptCommands: [],
                  appetizeCommands: [],
                  actionHistory: [],
                  screenContent: [],
                },
              ],
            };

            const newRuns = replaceItemFomArray(
              auxExecutionState.runs,
              auxExecutionState.runs.length - 1,
              newRun
            );

            auxExecutionState = {
              ...auxExecutionState,
              runs: newRuns,
            };

            setExecutionState(auxExecutionState);
          } else if (event.type === "PRE_REQUESTS_INIT") {
            const latestRun = auxExecutionState.runs.at(-1);
            const newRun: RunState = {
              ...latestRun,
              preRequests: [
                ...(latestRun.preRequests ?? []),
                {
                  screenshot: event.data.screenshot,
                },
              ],
            };

            const newRuns = replaceItemFomArray(
              auxExecutionState.runs,
              auxExecutionState.runs.length - 1,
              newRun
            );

            auxExecutionState = {
              ...auxExecutionState,
              runs: newRuns,
            };

            setExecutionState(auxExecutionState);
          } else if (event.type === "PRE_REQUESTS_RESPONSE") {
            const latestRun = auxExecutionState.runs.at(-1)!;
            const latestPreRequest = latestRun.preRequests!.at(-1)!;
            const newItem = event.data.responseValue;

            const newRun = {
              ...auxExecutionState.runs[auxExecutionState.runs.length - 1],
              preRequests: [
                ...latestRun.preRequests!.slice(0, -1),
                {
                  ...latestPreRequest,
                  item: newItem,
                },
              ],
            };

            const newRuns = replaceItemFomArray(
              auxExecutionState.runs,
              auxExecutionState.runs.length - 1,
              newRun
            );

            auxExecutionState = {
              ...auxExecutionState,
              runs: newRuns,
            };

            setExecutionState(auxExecutionState);
            setBuildLogsData((prev: BuildLogs) => ({
              ...prev,
              interactionLogs: [
                ...prev.interactionLogs,
                ...(event.data.interactionLogs ?? []),
              ],
            }));
          } else if (event.type === "TEARDOWN_REQUESTS_INIT") {
            const latestRun = auxExecutionState.runs.at(-1);
            if (latestRun == null) {
              return;
            }
            const newRun: RunState = {
              ...latestRun,
              teardownRequests: [
                ...(latestRun.teardownRequests ?? []),
                {
                  screenshot: event.data.screenshot,
                },
              ],
            };

            const newRuns = replaceItemFomArray(
              auxExecutionState.runs,
              auxExecutionState.runs.length - 1,
              newRun
            );

            auxExecutionState = {
              ...auxExecutionState,
              runs: newRuns,
            };

            setExecutionState(auxExecutionState);
          } else if (event.type === "TEARDOWN_REQUESTS_RESPONSE") {
            const latestRun = auxExecutionState.runs.at(-1);
            if (latestRun == null || latestRun.teardownRequests == null) {
              return;
            }
            const latestTeardownRequest = latestRun.teardownRequests.at(-1)!;
            const newItem = event.data.responseValue;

            const newRun = {
              ...latestRun,
              teardownRequests: [
                ...latestRun.teardownRequests.slice(0, -1),
                {
                  ...latestTeardownRequest,
                  item: newItem,
                },
              ],
            };

            const newRuns = replaceItemFomArray(
              auxExecutionState.runs,
              auxExecutionState.runs.length - 1,
              newRun
            );

            auxExecutionState = {
              ...auxExecutionState,
              runs: newRuns,
            };

            setExecutionState(auxExecutionState);
            setBuildLogsData((prev: BuildLogs) => ({
              ...prev,
              interactionLogs: [
                ...prev.interactionLogs,
                ...(event.data.interactionLogs ?? []),
              ],
            }));
          } else if (
            event.type === "GPT_RESPONSE" ||
            event.type === "LAST_INTERACTION_LOG"
          ) {
            const isGptResponse = event.type === "GPT_RESPONSE";
            const isLastInteractionLogAndServerExecution =
              event.type === "LAST_INTERACTION_LOG" && !fromEditor;

            const newStep: ExecutionStepState = {
              actionHistory: isGptResponse ? event.data.actionHistory : [],
              screenContent: isGptResponse ? event.data.screenContent : [],
              appetizeCommands: isGptResponse
                ? event.data.appetizeCommandsToExecute
                : [],
              gptCommands:
                isGptResponse || isLastInteractionLogAndServerExecution
                  ? event.data.gptStep
                  : [],
              screenshot: event.data.screenshot,
              serverExecutionParams: event.data.serverExecutionParams,
            };

            const prevRun =
              auxExecutionState.runs[auxExecutionState.runs.length - 1];

            const newExecutionSteps = isGptResponse
              ? replaceItemFomArray(
                  prevRun.executionSteps,
                  prevRun.executionSteps.length - 1,
                  newStep
                )
              : !fromEditor
              ? [...(prevRun?.executionSteps ?? []), newStep]
              : prevRun?.executionSteps ?? [];

            const newRun = {
              ...prevRun,
              executionSteps: newExecutionSteps,
            };

            auxExecutionState = {
              ...auxExecutionState,
              runs: replaceItemFomArray(
                auxExecutionState.runs,
                auxExecutionState.runs.length - 1,
                newRun
              ),
            };
            setExecutionState(auxExecutionState);
            if (isGptResponse || isLastInteractionLogAndServerExecution) {
              setBuildLogsData((prev: BuildLogs) => {
                const interactionLogWithAppetizeCommands = {
                  ...event.data.interactionLog,
                  appetizeCommands: newStep.appetizeCommands,
                };
                let newInteractionLogs = [
                  ...prev.interactionLogs,
                  interactionLogWithAppetizeCommands,
                ];
                if (event.data.shouldReplaceLastInteractionLog) {
                  newInteractionLogs = replaceItemFomArray(
                    prev.interactionLogs,
                    prev.interactionLogs.length - 1,
                    interactionLogWithAppetizeCommands
                  );
                }
                return {
                  ...prev,
                  interactionLogs: newInteractionLogs,
                };
              });
            }
          } else if (event.type === "UPDATE_EXECUTION_STEP") {
            console.log("UPDATE_EXECUTION_STEP", redactBase64ForLog(event.data));
            const { gptCommand, ...newEventData } = event.data;
            const parsedNewEventData = {
              ...newEventData,
              ...(gptCommand != null && { gptCommands: [gptCommand] }),
            };

            const prevRun =
              auxExecutionState.runs[auxExecutionState.runs.length - 1];

            const newStep = {
              ...prevRun.executionSteps.at(-1),
              ...parsedNewEventData,
            };

            const newExecutionSteps = replaceItemFomArray(
              prevRun.executionSteps,
              prevRun.executionSteps.length - 1,
              newStep
            );
            const newRun = {
              ...prevRun,
              executionSteps: newExecutionSteps,
            };
            auxExecutionState = {
              ...auxExecutionState,
              runs: replaceItemFomArray(
                auxExecutionState.runs,
                auxExecutionState.runs.length - 1,
                newRun
              ),
            };
            setExecutionState(auxExecutionState);
            setBuildLogsData((prev: BuildLogs) => {
              let newValue;
              const isUpdatingAppetizeCommands =
                "appetizeCommands" in parsedNewEventData;
              if (isUpdatingAppetizeCommands) {
                const replacementCommand =
                  parsedNewEventData.appetizeCommands[0];
                const targetSubstring = replacementCommand.split(";")[0].trim();
                const newGptCommands = prev?.interactionLogs
                  ?.at(-1)
                  ?.gptCommands?.map((command: string) =>
                    command.includes(targetSubstring)
                      ? command.replace(targetSubstring, replacementCommand)
                      : command
                  );

                newValue = {
                  gptCommands: newGptCommands,
                };
              } else {
                newValue = parsedNewEventData;
              }

              const newInteractionLogs = replaceItemFomArray(
                prev.interactionLogs,
                prev.interactionLogs.length - 1,
                {
                  ...prev.interactionLogs.at(-1),
                  ...newValue,
                }
              );

              return {
                ...prev,
                interactionLogs: newInteractionLogs,
              };
            });
          } else if (event.type === "RUN_COMPLETED") {
            await handleConcludeExecution(
              event,
              runs,
              {
                ...auxExecutionState,
              },
              companyData.name
            );
          } else if (event.type === "RUN_RETRY") {
            auxExecutionState = { ...defaultExecutionState, isRunning: true };
            setExecutionState(auxExecutionState);
            setBuildLogsData(RESET);
          }
        },
        error: async (error) => {
          console.log("there was an error", error);
          if (error?.type === "RUN_COMPLETED") {
            await handleConcludeExecution(
              error,
              runs,
              {
                ...auxExecutionState,
              },
              companyData.name
            );
          } else {
            let message = "Unexpected error";
            if (
              error?.type === "VIDEO_STREAMING_ERROR" ||
              error?.type === "APPIUM_SESSION_ERROR"
            ) {
              message = "Device connection was lost during test run";
            }
            await handleConcludeExecution(
              {
                data: {
                  status: "blocked",
                  message,
                  run: runs.at(-1),
                },
              },
              runs,
              {
                ...auxExecutionState,
              },
              companyData.name
            );
          }
        },
        complete: () => {
          console.log("Observable completed");
        },
      });
      auxExecutionState = { ...auxExecutionState, isRunning: true };
      setExecutionState(auxExecutionState);
      return () => {
        subscription.unsubscribe();
      };
    },
    [
      createObservables,
      settingsData,
      handleConcludeExecution,
      companyData.name,
      user,
    ]
  );

  const stopRunning = useCallback(() => {
    setExecutionState((prev) => ({ ...prev, isRunning: false }));
    const overlay = document.getElementById("overlay");
    if (overlay != null) overlay.style.display = "none";
  }, []);

  const resetEditor = useCallback(() => {
    setStopExecutionHandler(null);
    setExecutionState(defaultExecutionState);
  }, []);

  useEffect(() => {
    const handleParseLogs = async () => {
      const fromEditor = testSuite == null;
      if (!fromEditor || executionState.runs.length === 0) {
        return [];
      }

      console.log("executionState", redactBase64ForLog(executionState));
      const parsedLogs: CachedScreenData[][] = await Promise.all(
        executionState.runs.map(async (run) => {
          const runParsedSteps: CachedScreenData[] = [];

          if (run.preRequests != null && run.preRequests.length > 0) {
            runParsedSteps.unshift(
              ...run.preRequests.map((preRequest, index) =>
                preRequest.item != null
                  ? {
                      screenshotUrl: preRequest.screenshot,
                      appetizeCommand: "Executed network request",
                      gptCommand: preRequest.item.curl,
                      preRequestResponse: JSON.stringify(
                        preRequest.item.data,
                        null,
                        1
                      ),
                      statusCode: preRequest.item.status,
                      stepNumber: `${index + 1}`,
                      prompt: "Executing network request",
                      fromCache: false,
                      title: "pre-requests",
                    }
                  : {
                      screenshotUrl: preRequest.screenshot,
                      appetizeCommand: "Executed network request",
                      gptCommand: "",
                      stepNumber: `${index + 1}`,
                      prompt: "Generating...",
                      fromCache: false,
                      title: "pre-requests",
                    }
              )
            );
          }

          const runSteps: CachedScreenData[] = await Promise.all(
            run.executionSteps.map(async (step, index) => {
              const annotatedScreenshot = await annotateScreenshot(
                step.screenshot,
                step.appetizeCommands
              );

              const {
                reasoning,
                commands,
                step_number,
                step_description,
                fromCache,
                step_id,
              } = parseGptCommandsResponse(step.gptCommands ?? []);

              const stepNumberInt = parseInt(step_number);
              const promptText = getPromptText(
                stepNumberInt,
                run.commands,
                step_description,
                step_id,
                run?.promptSteps ?? []
              );
              return {
                annotatedScreenshotUrl: annotatedScreenshot,
                screenshotUrl: step.screenshot,
                ...(step.voiceCommandUrl && {
                  voiceCommandUrl: step.voiceCommandUrl,
                }),
                ...(step.listenedAudioUrl && {
                  listenedAudioUrl: step.listenedAudioUrl,
                }),
                appetizeCommand: step.appetizeCommands?.join("\n") ?? [],
                gptCommand: commands,
                reasoning,
                stepNumber: step_number,
                prompt: promptText ?? "Generating...",
                fromCache,
                title:
                  executionState.runs[executionState.runs.length - 1].title !==
                  run.title
                    ? run.title
                    : "parent",
                serverExecutionParams: step.serverExecutionParams,
              };
            })
          );

          runParsedSteps.push(...runSteps);

          if (run.teardownRequests != null && run.teardownRequests.length > 0) {
            runParsedSteps.push(
              ...run.teardownRequests.map((teardownRequest, index) =>
                teardownRequest.item != null
                  ? {
                      screenshotUrl: teardownRequest.screenshot,
                      appetizeCommand: "Executed network request",
                      gptCommand: teardownRequest.item.curl,
                      preRequestResponse: JSON.stringify(
                        teardownRequest.item.data,
                        null,
                        1
                      ),
                      statusCode: teardownRequest.item.status,
                      stepNumber: `${index + 1}`,
                      prompt: "Executing network request",
                      fromCache: false,
                      title: "teardown-requests",
                    }
                  : {
                      screenshotUrl: teardownRequest.screenshot,
                      appetizeCommand: "Executed network request",
                      gptCommand: "",
                      stepNumber: `${index + 1}`,
                      prompt: "Generating...",
                      fromCache: false,
                      title: "teardown-requests",
                    }
              )
            );
          }

          return runParsedSteps;
        })
      );

      const flattenedParsedLogs = parsedLogs.flat();
      setParsedExecutionLogs(flattenedParsedLogs);
    };
    handleParseLogs();
  }, [executionState, testSuite]);

  useEffect(() => {
    return () => {
      stopExecutionHandler?.();
    };
  }, [stopExecutionHandler]);

  return {
    startExecution,
    stopExecution: stopExecutionHandler,
    startedRunningParentRunMutation,
    concludedParentRunMutation,
    resetEditor,
    executionState,
    parsedExecutionLogs,
  };
};

export default useExecuteSteps;

export type { ExecutionStepState };
